import Button from '@components/Button';
import { HELPDESK_NUMBER } from '@utils/constants';
import { isChunkLoadError } from '@utils/lazy-with-retry';
import MLALogger from '@utils/logger';
import React from 'react';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    // When this changes (e.g. the route), a caught error is cleared so the new content can render.
    resetKey?: string;
}

interface ErrorBoundaryState {
    error: Error | null;
}

/**
 * Replaces the white screen React leaves behind after an uncaught render error (e.g. a ChunkLoadError
 * that lazyWithRetry couldn't recover from) with a message and a reload button.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public state: ErrorBoundaryState = { error: null };

    public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { error };
    }

    public componentDidCatch(error: Error): void {
        // React doesn't pass errors a boundary catches on to window.onerror in production, so report
        // them to New Relic ourselves to keep them visible.
        MLALogger.Log(['ErrorBoundary'], error, 'error');
    }

    public componentDidUpdate(prevProps: ErrorBoundaryProps): void {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null });
        }
    }

    public render(): React.ReactNode {
        const { error } = this.state;
        if (!error) {
            return this.props.children;
        }

        return (
            <div
                className="ErrorBoundary"
                role="alert"
            >
                <h2 className="ErrorBoundary__title">
                    {isChunkLoadError(error) ? "We couldn't load this page" : 'Something went wrong'}
                </h2>
                <p className="ErrorBoundary__text">
                    {isChunkLoadError(error)
                        ? 'This can happen after an eNVD update or if your internet connection dropped. Please check your connection and reload the page.'
                        : `Please reload the page. If the problem continues, call ISC Helpdesk at ${HELPDESK_NUMBER}.`}
                </p>
                <Button
                    buttonType="primary"
                    action={() => window.location.reload()}
                >
                    Reload page
                </Button>
                <style jsx>{`
                    .ErrorBoundary {
                        max-width: 640px;
                        margin: 80px auto;
                        padding: 0 16px;
                        text-align: center;
                    }

                    .ErrorBoundary__text {
                        margin: 16px 0 24px;
                    }
                `}</style>
            </div>
        );
    }
}

export default ErrorBoundary;
